import { RichTextElement } from "@kontent-ai/gatsby-components";
import React from "react";
import {
  ComponentProps,
  ComponentWidth,
  KontentRichText,
  StringValue,
  Vector,
} from "../../../types";
import parse, { DomElement } from "html-react-parser";
import * as styles from "./ComparisonTable.module.css";
import { SectionWrapper } from "../../containers/SectionWrapper";
import { CustomRichTextElement } from "../../elements/CustomRichTextElement/CustomRichTextElement";

interface Props extends ComponentProps, ComponentWidth {
  heading?: string;
  text?: KontentRichText;
  table: KontentRichText;
  rightVector?: Vector;
  backgroundVector?: Vector;
}

export const ComparisonTable: React.FC<Props> = ({
  heading,
  text,
  table,
  rightVector,
  backgroundVector,
  width,
  ...props
}) => {
  const classes = [styles.comparisontable];
  width === "contained" && classes.push(styles.contained);

  return (
    <SectionWrapper {...props}>
      <div className={classes.join(" ")} data-kontent-element-codename="table">
        {(heading || text) && (
          <div className={styles.heading}>
            <h2>{heading}</h2>
            {text && (
              <div className={styles.text}>
                <CustomRichTextElement content={text} />
              </div>
            )}
          </div>
        )}
        <div className={styles.tableContainer}>
          <hr />
          <RichTextElement
            {...table}
            resolveDomNode={(domNode: DomElement, domToReact: Function) => {
              if (domNode.name === "table") {
                const tBody = domNode?.children?.find(
                  (node) => node.name === "tbody"
                );
                const rows = tBody?.children?.filter(
                  (node) => node.name === "tr"
                );
                const headRow = rows[0];
                const bodyRows = rows.slice(1);

                return (
                  <div className={styles.table}>
                    <table className={styles.fullComparison}>
                      <thead>
                        <tr>
                          {headRow?.children?.map(
                            (column: any, colIdx: number) => {
                              return (
                                <th key={`desktopTable-${colIdx}`}>
                                  {domToReact(column.children)}
                                </th>
                              );
                            }
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {bodyRows?.map((row: any, rowIdx: number) => (
                          <tr key={`desktopTable-r${rowIdx}`}>
                            {row.children?.map(
                              (column: any, colIdx: number) => {
                                if (colIdx === 0) {
                                  return (
                                    <th
                                      key={`desktopTable-r${rowIdx}c${colIdx}`}
                                    >
                                      {domToReact(column.children)}
                                    </th>
                                  );
                                }
                                return (
                                  <td key={`desktopTable-r${rowIdx}c${colIdx}`}>
                                    {domToReact(column.children)}
                                  </td>
                                );
                              }
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className={styles.singleOptionComparison}>
                      {headRow?.children?.map((column, dataIdx) => {
                        if (dataIdx === 0) return;
                        return (
                          <table key={`mobileTable-${dataIdx}`}>
                            <thead>
                              <tr>
                                <th>{domToReact(column.children)}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bodyRows?.map((row: any, rowIdx: number) => (
                                <tr key={`mT${dataIdx}r${rowIdx}`}>
                                  {row.children?.map(
                                    (column: any, colIdx: number) => {
                                      if (colIdx === 0) {
                                        return (
                                          <th
                                            key={`mT${dataIdx}r${rowIdx}c${colIdx}`}
                                          >
                                            {domToReact(column.children)}
                                          </th>
                                        );
                                      }

                                      if (colIdx !== dataIdx) return null;

                                      return (
                                        <td
                                          key={`mT${dataIdx}r${rowIdx}c${colIdx}`}
                                        >
                                          {domToReact(column.children)}
                                        </td>
                                      );
                                    }
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        );
                      })}
                    </div>
                  </div>
                );
              }
            }}
          />
          {backgroundVector?.item && (
            <div
              className={styles.background}
              data-element-kontent-codename="background_vector"
            >
              <img
                src={backgroundVector?.item?.url}
                alt={backgroundVector?.alt}
              />
            </div>
          )}
          {rightVector?.item && (
            <div
              className={styles.right}
              data-element-kontent-codename="right_vector"
            >
              <img src={rightVector?.item?.url} alt={rightVector?.alt} />
            </div>
          )}
          <hr />
        </div>
      </div>
    </SectionWrapper>
  );
};

const createElement = (
  elementName: string,
  options?: { selfClosing?: boolean }
) => {
  const element = options?.selfClosing
    ? parse(`<${elementName} />`)
    : parse(`<${elementName}></${elementName}>`);

  return element;
};
